import { useQueryClient } from '@tanstack/react-query';
import { type FC, useCallback, useEffect, useMemo, useRef } from 'react';
import { batch } from 'react-redux';
import { debounce } from 'throttle-debounce';

import { useReduxState } from '@/app/shared/context/ReduxStoreContext';

import { useMapStoreContext } from '@/microfrontends/map/shared/context/mapStoreContext';
import { convertMapBoundsToJsonQueryBbox } from '@/microfrontends/map/shared/utils/map/convertMapBoundsToJsonQueryBbox';

import { FetchGetClustersLoadService } from '@/packages/query-state/shared/store/search-offers-index/v2/get-clusters-for-map/load';
import { boundActionsFactory } from '@/packages/redux-state/store';
import { filtersBboxSelector } from '@/packages/redux-state/store/slices/jsonQuery';
import { listingLayoutViewModeSelector } from '@/packages/redux-state/store/slices/listingLayout/selectors/dumb';

const DEBOUNCE_TIMEOUT = 1000;

const useBoundActions = boundActionsFactory(reduxActions => ({
  setIsCurrentAreaSearchActive: reduxActions.mapControls.setIsCurrentAreaSearchActive,
  setMapConfiguration: reduxActions.mapConfiguration.setMapConfiguration,
  updateFiltersBbox: reduxActions.jsonQuery.updateBbox,
}));

/**
 * Переработать этот менеджер, явно можно упростить, пока как будто бы лишняя логика
 */
export const MapBoundsUpdateManager: FC = () => {
  const { map } = useMapStoreContext();
  const actions = useBoundActions();
  const queryClient = useQueryClient();
  const fetchGetClustersLoadService = useMemo(() => new FetchGetClustersLoadService(queryClient), [queryClient]);

  const reduxState = useReduxState();

  const mapSize = useRef(map.container.getSize());

  const prevBbox = useRef(filtersBboxSelector(reduxState.getState()));

  const updateMapConfiguration = useCallback(() => {
    const bounds = map.getBounds();

    batch(() => {
      actions.setMapConfiguration({
        bounds,
        center: map.getCenter(),
        zoom: map.getZoom(),
      });

      /**
       * @description
       * Если через фильтры сбросили границы, то карта перестроится через хук useMapUpdateOnFiltersChange.
       * В таком случае нам не нужно обновлять границы в jsonQuery после перестроения.
       * Иначе обновляем bbox в jsonQuery.
       */
      const bbox = filtersBboxSelector(reduxState.getState());
      const bboxRemoved = Boolean(prevBbox.current) && !bbox;

      if (!bboxRemoved) {
        const nextBbox = convertMapBoundsToJsonQueryBbox(bounds);

        actions.updateFiltersBbox(nextBbox);

        prevBbox.current = nextBbox;
      } else {
        prevBbox.current = undefined;
      }
    });
  }, [actions, map, reduxState]);

  const handleBoundsChange = useCallback(() => {
    const newMapSize = map.container.getSize();

    const isMapSizeChanged = mapSize.current !== newMapSize;

    if (isMapSizeChanged) {
      mapSize.current = newMapSize;
    }

    const viewMode = listingLayoutViewModeSelector(reduxState.getState());

    if (viewMode === 'list' || isMapSizeChanged) {
      return;
    }

    fetchGetClustersLoadService.cancelQuery().then(() => {
      updateMapConfiguration();
    });
  }, [fetchGetClustersLoadService, map, reduxState, updateMapConfiguration]);

  const handleBoundsChangeDebounced = useMemo(
    () => debounce(DEBOUNCE_TIMEOUT, handleBoundsChange),
    [handleBoundsChange],
  );

  useEffect(() => {
    map.events.add('boundschange', handleBoundsChangeDebounced);

    return () => {
      map.events.remove('boundschange', handleBoundsChangeDebounced);
    };
  }, [map.events, handleBoundsChangeDebounced]);

  return null;
};
